import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { registerModalBackHandler } from '../lib/native';
import {
  Button,
  Spinner,
} from './design-system';

interface SupportModalProps {
  onClose: () => void;
}

export function SupportModal({ onClose }: SupportModalProps) {
  const { user, profile } = useAuth();

  // Android hardware back button closes this modal instead of navigating
  // the route underneath it.
  useEffect(() => registerModalBackHandler(onClose), [onClose]);
  const [name, setName] = useState(profile?.full_name ?? '');
  const email = user?.email ?? '';
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!name.trim() || !email.trim() || !subject.trim() || !body.trim()) return;
    setSending(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('send-support-email', {
        body: { name: name.trim(), email: email.trim(), subject: subject.trim(), body: body.trim() },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  }

  const inputClass = 'rounded-xl border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none w-full';

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end md:items-center justify-center" onClick={onClose}>
      <div
        className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-md max-h-dvh-92 overflow-y-auto pb-safe-or-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-app-card z-10 px-5 pt-5 pb-3 border-b border-app-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-app-fg">Contact Support</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-app-bg border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0 sk-hit">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-5 flex flex-col gap-4">
          {sent ? (
            <div className="flex flex-col items-center text-center gap-3 py-6">
              <div className="w-12 h-12 rounded-full bg-success/15 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-success">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h4 className="text-base font-semibold text-app-fg mb-1">Message sent</h4>
                <p className="text-sm text-app-muted">We'll get back to you at <span className="text-app-fg font-medium">{email}</span> as soon as possible.</p>
              </div>
              <Button onClick={onClose} variant="ghost" size="sm">Close</Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-app-fg-2">Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputClass} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-app-fg-2">Email</label>
                  <input type="email" value={email} readOnly aria-readonly="true" title="Replies go to your account email" className={`${inputClass} opacity-70`} />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-app-fg-2">Subject</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What do you need help with?" className={inputClass} />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-app-fg-2">Message</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  placeholder="Describe your issue or question…"
                  className={`${inputClass} resize-none`}
                />
              </div>

              {error && <p className="text-xs text-crimson">{error}</p>}

              <div className="flex items-center justify-end gap-3 pt-1">
                <button onClick={onClose} className="text-sm text-app-muted hover:text-app-fg sk-hit">Cancel</button>
                <Button
                  onClick={() => void handleSubmit()}
                  disabled={sending || !name.trim() || !email.trim() || !subject.trim() || !body.trim()}
                >
                  {sending ? <Spinner size="sm" className="text-ink" /> : 'Send message'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
